import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs, Heading } from '@digdir/designsystemet-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { OmBoligenForm } from '../pages/omBoligen/form';
import { OmBoligenFormData } from '../pages/omBoligen/form/types';
import { useCreateV2025Registration } from '../hooks/useRegistrationV2025Form/useCreateV2025Registration';
import { useBuildingData } from '../../../hooks/useBuildingDetails/buildingDetailsHooks';
import { BuildingCard } from '../pages/omBoligen/buildingCard';
import { RegistrationCommonStep } from '../../../utils/navigation';
import { useGetUserInputReadOnly } from '../../registration/hooks/useGetUserInput';
import { fromUserInputToFormData } from '../pages/omBoligen/form/utils/dataMapper';
import { PageWrapper } from '../pages/solceller';
import { initRegistration, updateRegistration as updateRegistrationAction } from '../../../store/registration/actions';

import { Stepper } from './stepper';
import { BreadcrumbsWrapper, MainContentWrapper, SidebarPageLayout, SidebarWrapper } from './styledComponents';
import { SidebarPageLoader } from './pageLoader';
import { ErrorState } from './errorState';

export const NewRegistrationPage = () => {
  const { buildingId, registrationToCopyId } = useParams();
  const { building, buildingLoading, buildingError } = useBuildingData(buildingId);
  const { userInput, userInputLoading } = useGetUserInputReadOnly(registrationToCopyId);
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const { createRegistration, createIsLoading } = useCreateV2025Registration();
  const navigate = useNavigate();

  // Always reset Redux store when entering new registration page to clear any previous session data
  useEffect(() => {
    dispatch(initRegistration());
  }, [dispatch]);

  // When copying from a previous registration, populate Redux store with the previous userInput
  // so that Oppvarming and Ventilasjon data are available when creating the new registration
  // This must happen after initRegistration to ensure clean state
  useEffect(() => {
    if (userInput && registrationToCopyId) {
      dispatch(updateRegistrationAction({ userInput }));
    }
  }, [userInput, registrationToCopyId, dispatch]);

  const onSubmit = (formData: OmBoligenFormData) => {
    createRegistration(
      { formData: { ...formData, kommunenummer: building?.adresse.kommuneNummer } },
      {
        onSuccess: (data) => {
          navigate(`../${data.registreringId}/${RegistrationCommonStep.VENTILATION}`);
        }
      }
    );
  };

  if (userInputLoading || buildingLoading) {
    return <SidebarPageLoader />;
  }

  if (buildingError) {
    return <ErrorState title={t('building.error')} traceId={buildingError.traceId} />;
  }

  if (!building) {
    return null;
  }

  const defaultValues = fromUserInputToFormData({ userInput });

  return (
    <SidebarPageLayout>
      <BreadcrumbsWrapper>
        <Breadcrumbs>
          <Breadcrumbs.Link asChild aria-label={t('registrering.back')}>
            <Link to={`/bygg/${building?.energiObjektId}`}>{t('registrering.back')}</Link>
          </Breadcrumbs.Link>
        </Breadcrumbs>
      </BreadcrumbsWrapper>
      <SidebarWrapper>
        <Stepper />
      </SidebarWrapper>
      <MainContentWrapper>
        <PageWrapper>
          <Heading level={1} data-size="lg">
            {t('registrering.heading')}
          </Heading>
          <BuildingCard building={building} />
          <OmBoligenForm
            onSubmit={onSubmit}
            isLoading={createIsLoading}
            buildingId={building.energiObjektId}
            defaultValues={defaultValues}
          />
        </PageWrapper>
      </MainContentWrapper>
    </SidebarPageLayout>
  );
};
